import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import { Alert, Anchor, Button, PasswordInput, Stack } from "@mantine/core"
import { useCallback, useState } from "react"
import { AuthShell } from "../../-auth-shared/AuthShell"
import type { IProps } from "./interfaces"

export const ResetPasswordView = ({
  logo,
  appName,
  product,
  loginBackground,
  loading,
  error,
  success,
  tokenMissing,
  onSubmit,
}: IProps): React.JSX.Element => {
  const [password, setPassword] = useState("")
  const [confirm, setConfirm] = useState("")
  const { i18n } = useLingui()

  const mismatch = confirm.length > 0 && password !== confirm

  const handleSubmit = useCallback(
    (e: React.FormEvent): void => {
      e.preventDefault()
      if (password !== confirm) return
      onSubmit(password)
    },
    [password, confirm, onSubmit],
  )

  return (
    <AuthShell
      logo={logo}
      appName={appName}
      product={product}
      loginBackground={loginBackground}
      title={<Trans>Choose a new password</Trans>}
    >
      {tokenMissing ? (
        <Stack>
          <Alert color="danger" variant="light">
            <Trans>This reset link is invalid or has expired. Please request a new one.</Trans>
          </Alert>
          <Anchor href="/forgot-password" ta="center" size="sm">
            <Trans>Request a new link</Trans>
          </Anchor>
        </Stack>
      ) : success ? (
        <Stack>
          <Alert color="success" variant="light">
            <Trans>Your password has been updated. You can now sign in.</Trans>
          </Alert>
          <Anchor href="/login" ta="center" size="sm">
            <Trans>Go to sign in</Trans>
          </Anchor>
        </Stack>
      ) : (
        <form onSubmit={handleSubmit}>
          <Stack>
            {error ? (
              <Alert color="danger" variant="light">
                {error}
              </Alert>
            ) : null}
            <PasswordInput
              label={i18n._(msg`New password`)}
              placeholder={i18n._(msg`At least 8 characters`)}
              value={password}
              onChange={(e) => setPassword(e.currentTarget.value)}
              required
              autoFocus
            />
            <PasswordInput
              label={i18n._(msg`Confirm password`)}
              value={confirm}
              onChange={(e) => setConfirm(e.currentTarget.value)}
              error={mismatch ? i18n._(msg`Passwords don't match`) : undefined}
              required
            />
            <Button type="submit" fullWidth loading={loading} disabled={password.length < 8 || mismatch}>
              <Trans>Update password</Trans>
            </Button>
          </Stack>
        </form>
      )}
    </AuthShell>
  )
}
